import { T } from 'gt-react';
import { Label } from 'src/components/ui/label';
import { Checkbox } from 'src/components/ui/checkbox';
import type { Sd849ResponseMethod } from '@erp/shared-types';

interface Sd849ResponseChannelsFieldProps {
  channels: Sd849ResponseMethod[];
  onChange: (channels: Sd849ResponseMethod[] | undefined) => void;
}

/**
 * Picks the channel(s) a supplier answers 844s on.
 *
 * Two independent checkboxes rather than the single-choice `Select` this
 * replaces, because the two channels are two independent facts and some
 * suppliers use both. The old dropdown made a dual-channel supplier
 * unrepresentable, and — since the "849 Email Addresses" input was gated on the
 * same value — also made that input unreachable for any supplier whose dropdown
 * said `edi` or nothing at all.
 *
 * Split out of the config modal so this rule is testable without the modal's
 * data layer: the interesting behaviour is that toggling one channel leaves the
 * other alone, which is exactly what the `Select` could not do.
 */
export function Sd849ResponseChannelsField({
  channels,
  onChange,
}: Sd849ResponseChannelsFieldProps) {
  const toggle = (channel: Sd849ResponseMethod, checked: boolean) => {
    // Rebuild in a fixed order so the stored value doesn't churn with click order.
    const next = (['edi', 'email'] as const).filter((c) =>
      c === channel ? checked : channels.includes(c),
    );
    // No channel selected is "nobody has said", which is `undefined` — storing
    // `[]` would record a deliberate empty choice that nobody made.
    onChange(next.length > 0 ? [...next] : undefined);
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs">
        <T context="form label for the channels a supplier sends EDI 849 responses on">
          849 Response Method
        </T>
      </Label>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="sd_849_response_method_edi"
            checked={channels.includes('edi')}
            onCheckedChange={(checked) => toggle('edi', checked === true)}
          />
          <Label
            htmlFor="sd_849_response_method_edi"
            className="text-xs cursor-pointer"
          >
            <T context="response method option EDI for 849 documents">EDI</T>
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="sd_849_response_method_email"
            checked={channels.includes('email')}
            onCheckedChange={(checked) => toggle('email', checked === true)}
          />
          <Label
            htmlFor="sd_849_response_method_email"
            className="text-xs cursor-pointer"
          >
            <T context="response method option email for 849 documents">
              Email
            </T>
          </Label>
        </div>
      </div>
    </div>
  );
}
